import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Upload, Loader2, CheckCircle2, Image as ImageIcon, Video, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { mediaAPI, getErrorMessage } from "../../services/api";

const ACCEPTED_TYPES = "image/*,video/*,application/pdf";

export default function PublicMediaSubmissionPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [submitterName, setSubmitterName] = useState("");
  const [submitterEmail, setSubmitterEmail] = useState("");
  const [submitterNote, setSubmitterNote] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const fileKind = useMemo(() => {
    if (!selectedFile) return null;
    if (selectedFile.type.startsWith("image/")) return "image";
    if (selectedFile.type.startsWith("video/")) return "video";
    return "document";
  }, [selectedFile]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!token) {
        throw new Error(t("media_submission_invalid_token", "This submission link is invalid."));
      }
      if (!selectedFile) {
        throw new Error(t("media_submission_file_required", "Choose a file to continue."));
      }
      const formData = new FormData();
      formData.append("token", token);
      formData.append("file", selectedFile);
      if (submitterName.trim()) formData.append("submitter_name", submitterName.trim());
      if (submitterEmail.trim()) formData.append("submitter_email", submitterEmail.trim());
      if (submitterNote.trim()) formData.append("submitter_note", submitterNote.trim());
      return mediaAPI.submitPublicAsset(formData);
    },
    onSuccess: () => setSubmitted(true),
  });

  if (submitted) {
    return (
      <div className="min-h-screen bg-n-page px-4 py-12">
        <div className="mx-auto max-w-2xl rounded-n border border-n-border bg-n-card p-8 shadow-n-sm">
          <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-n-green-bg text-n-green">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-n-text">
            {t("media_submission_success_title", "Submission received")}
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-6 text-n-text-md">
            {t(
              "media_submission_success_body",
              "Your file is now waiting in the workspace review queue. The team can approve it into the Media Library or follow up with you if they need more context."
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-n-page px-4 py-12">
      <div className="mx-auto max-w-3xl rounded-n border border-n-border bg-n-card shadow-n-sm">
        <div className="border-b border-n-border px-8 py-7">
          <div className="inline-flex items-center rounded-full border border-n-green/20 bg-n-green-bg px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-n-green">
            {t("media_submission_badge", "Creator submission")}
          </div>
          <h1 className="mt-4 text-3xl font-semibold tracking-tight text-n-text">
            {t("media_submission_title", "Send a file to the team")}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-n-text-md">
            {t(
              "media_submission_subtitle",
              "Upload an image, video, or PDF with a short note. It will land in the Media Library review queue so the team can approve it into the workspace."
            )}
          </p>
        </div>

        <div className="grid gap-8 px-8 py-8 lg:grid-cols-[1.2fr_0.8fr]">
          <form
            className="space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              submitMutation.mutate();
            }}
          >
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-n-text-lt">
                {t("media_submission_name", "Your name")}
              </label>
              <input
                value={submitterName}
                onChange={(e) => setSubmitterName(e.target.value)}
                className="h-11 w-full rounded-n-sm border border-n-border bg-white px-3 text-sm text-n-text outline-none transition focus:border-n-green focus:ring-2 focus:ring-n-green/10"
                placeholder={t("media_submission_name_placeholder", "Jane Smith")}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-n-text-lt">
                {t("media_submission_email", "Email")}
              </label>
              <input
                type="email"
                value={submitterEmail}
                onChange={(e) => setSubmitterEmail(e.target.value)}
                className="h-11 w-full rounded-n-sm border border-n-border bg-white px-3 text-sm text-n-text outline-none transition focus:border-n-green focus:ring-2 focus:ring-n-green/10"
                placeholder={t("media_submission_email_placeholder", "jane@example.com")}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-n-text-lt">
                {t("media_submission_note", "Context for the team")}
              </label>
              <textarea
                value={submitterNote}
                onChange={(e) => setSubmitterNote(e.target.value)}
                rows={5}
                className="w-full rounded-n-sm border border-n-border bg-white px-3 py-3 text-sm text-n-text outline-none transition focus:border-n-green focus:ring-2 focus:ring-n-green/10"
                placeholder={t(
                  "media_submission_note_placeholder",
                  "What is this asset for, who is it from, and how should the team use it?"
                )}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-n-text-lt">
                {t("media_submission_file", "File")}
              </label>
              <label className="flex cursor-pointer items-center gap-3 rounded-n border border-dashed border-n-border bg-n-page px-4 py-5 transition hover:border-n-green/40 hover:bg-white">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-n-green-bg text-n-green">
                  <Upload className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-n-text">
                    {selectedFile
                      ? selectedFile.name
                      : t("media_submission_file_cta", "Choose an image, video, or PDF")}
                  </p>
                  <p className="mt-0.5 text-xs text-n-text-md">
                    {selectedFile
                      ? `${Math.round(selectedFile.size / 1024)} KB`
                      : t("media_submission_file_help", "Uploads go straight into the workspace review queue.")}
                  </p>
                </div>
                <input
                  type="file"
                  accept={ACCEPTED_TYPES}
                  className="hidden"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                />
              </label>
            </div>

            {submitMutation.isError && (
              <div className="rounded-n-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {getErrorMessage(
                  submitMutation.error,
                  t("media_submission_failed", "We could not upload that file.")
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={submitMutation.isPending || !selectedFile || !token}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-n-sm bg-n-green px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("media_submission_uploading", "Uploading")}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  {t("media_submission_submit", "Send to workspace")}
                </>
              )}
            </button>
          </form>

          <aside className="rounded-n border border-n-border bg-n-page p-5">
            <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-n-text-lt">
              {t("media_submission_review_heading", "What happens next")}
            </h2>
            <ul className="mt-4 space-y-4 text-sm leading-6 text-n-text-md">
              <li className="flex gap-3">
                <span className="mt-1 inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white text-n-green shadow-sm">
                  {fileKind === "video" ? (
                    <Video className="h-4 w-4" />
                  ) : fileKind === "document" ? (
                    <FileText className="h-4 w-4" />
                  ) : (
                    <ImageIcon className="h-4 w-4" />
                  )}
                </span>
                <span>{t("media_submission_step_one", "The team reviews the file and context in the Media Library queue.")}</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-1 inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white text-n-green shadow-sm">2</span>
                <span>{t("media_submission_step_two", "Approved assets are promoted into the shared library with usage-ready metadata.")}</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-1 inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white text-n-green shadow-sm">3</span>
                <span>{t("media_submission_step_three", "Rejected files stay out of the workspace until the team asks for a better version or more context.")}</span>
              </li>
            </ul>
            {!token && (
              <div className="mt-5 rounded-n-sm border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                {t("media_submission_invalid_token", "This submission link is invalid.")}
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
